import {
  FunctionComponent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import debounce from "lodash.debounce";
import Icon from "@mdi/react";
import { mdiCloseCircleOutline } from "@mdi/js";
import DrawerModal, { DrawerModalLoaction } from "../drawer_modal";
import "../../../../styles/responsive_shrink.css";

export const FilterClearButton: FunctionComponent<{
  onClick: () => void;
  title?: string;
}> = ({ onClick, title = "Clear" }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="text-red-500 hover:text-red-700 transition-colors shrink-0"
  >
    <Icon path={mdiCloseCircleOutline} size={0.7} />
  </button>
);

/** Free-text filter styled like the async filter pickers: applies while
 * typing (debounced), clear cross only when there is text. */
export const TextFilterInput: FunctionComponent<{
  id?: string;
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  debounceMs?: number;
}> = ({ id, value, onChange, placeholder = "Search", debounceMs = 500 }) => {
  const [text, setText] = useState(value ?? "");
  // Sync external changes (Reset / Clear Filter) without trimming text the
  // user is still typing.
  useEffect(
    () => setText((t) => (t.trim() === (value ?? "") ? t : (value ?? ""))),
    [value],
  );
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const emit = useMemo(
    () =>
      debounce(
        (v: string) => onChangeRef.current(v.trim() || undefined),
        debounceMs,
      ),
    [debounceMs],
  );
  useEffect(() => () => emit.cancel(), [emit]);
  return (
    <div className="flex items-center gap-1 w-full">
      <div className="flex items-center border rounded-md w-full bg-white">
        <input
          id={id}
          autoComplete="off"
          type="text"
          value={text}
          placeholder={placeholder}
          onChange={(e) => {
            setText(e.target.value);
            emit(e.target.value);
          }}
          className="w-full text-xs px-2 h-8 rounded-md focus:outline-none bg-transparent"
        />
      </div>
      {text && (
        <FilterClearButton
          onClick={() => {
            emit.cancel();
            setText("");
            onChangeRef.current(undefined);
          }}
        />
      )}
    </div>
  );
};

interface FilterSheetProps {
  show: boolean;
  onClose: () => void;
  onApply?: () => void;
  onReset?: () => void;
  title?: string;
  gridClassName?: string;
  children: ReactNode;
}

const FilterSheet: FunctionComponent<FilterSheetProps> = ({
  show,
  onClose,
  onApply,
  onReset,
  title = "Filters",
  gridClassName = "grid grid-cols-1 md:grid-cols-12 gap-3",
  children,
}) => {
  const footer = (onApply || onReset) && (
    <div className="flex gap-2 justify-end mt-4">
      {onReset && (
        <button
          onClick={onReset}
          className="px-4 py-2 border border-gray-300 rounded text-xs font-semibold text-gray-700 hover:bg-gray-50"
        >
          Reset
        </button>
      )}
      {onApply && (
        <button
          onClick={onApply}
          className="px-4 py-2 bg-myPrimaryColor text-white rounded text-xs font-semibold hover:bg-mySecondaryColor"
        >
          Apply
        </button>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop: inline panel */}
      {show && (
        <div className="hidden md:block rounded-lg border border-gray-300 shadow-sm bg-white p-3 mt-2">
          <div className={`filter-compact ${gridClassName}`}>{children}</div>
          {footer}
        </div>
      )}

      {/* Mobile: drawer/sheet */}
      <div className="md:hidden">
        {show && (
          <DrawerModal
            show={show}
            onClose={onClose}
            title={title}
            location={DrawerModalLoaction.right}
          >
            <div className="filter-compact p-4 flex flex-col gap-3">
              {children}
              <div className="flex gap-2 justify-end mt-2">
                {onReset && (
                  <button
                    onClick={onReset}
                    className="px-4 py-2 border border-gray-300 rounded text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Reset
                  </button>
                )}
                <button
                  onClick={() => {
                    onApply?.();
                    onClose();
                  }}
                  className="px-4 py-2 bg-myPrimaryColor text-white rounded text-xs font-semibold hover:bg-mySecondaryColor"
                >
                  Apply
                </button>
              </div>
            </div>
          </DrawerModal>
        )}
      </div>
    </>
  );
};

export default FilterSheet;
